// ┌──────────────────────────────────────────────┐
// │ Exit Intent → Braze (E20-1187, E20-1300)     │
// └──────────────────────────────────────────────┘
// Fires a Braze `exit_intent` custom event once per page load, US market only
// (CA is out of scope — the abandoned-cart program is US-only). Ports the
// detection logic from the legacy GTM tag as-is:
//   - Desktop: cursor leaves the viewport at the top.
//   - Mobile/tablet: scroll down >= 50% of page height, then back up >= 20%.
//   - Fallback: 60s of inactivity.
//
// Cart data is sourced from Shopify's /cart.js (the legacy tag read GTM
// dataLayer variables, which don't exist here). Payload field names follow
// E20-1300's example, but price fields are converted from /cart.js's
// whole-cent integers to dollar decimals to match Braze's own conventions.
//
// SDK readiness is handled by the shared util.braze helper.

import { logCustomEventOrThrow, sendToBraze } from 'util.braze';

const market = document.body.dataset.market || '';

const EVENT_NAME = 'exit_intent';
const INACTIVITY_MS = 60000;

const root = () => window.Shopify?.routes?.root || '/';

const getCart = () =>
  fetch(`${root()}cart.js`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((r) => {
    if (!r.ok) throw new Error(`cart.js responded with ${r.status}`);
    return r.json();
  });

// A cart line is "restorable" via a plain cart permalink if a permalink
// restore would reproduce it faithfully — see buildCartPermalink() below for
// why permalinks can't carry line-item properties or selling plans. Three
// kinds of line can't survive that: protection-plan lines (identified by
// _parent_sku, linked to their machine via bundle properties — see
// bfx-protection-plan.js), gift cards addressed to a recipient (identified by
// __shopify_send_gift_card_to_recipient, set by
// snippets/gift-card-recipient-form.liquid alongside the recipient
// email/name/message/send-on properties) — a restored line would silently
// become a plain self-purchase gift card, losing the recipient entirely — and
// subscription lines (identified by a non-null selling_plan_allocation) — a
// permalink can only restore the variant as a one-time purchase, or fails
// outright for a subscription-only product. The whole payload (products,
// totals, and the permalink) is scoped to this same subset, so the recovery
// email never shows or totals something the link can't actually put back in
// the customer's checkout.
const restorableItems = (cart) =>
  cart.items.filter(
    (item) =>
      !item.properties?._parent_sku &&
      !item.properties?.__shopify_send_gift_card_to_recipient &&
      !item.selling_plan_allocation
  );

// Cart permalink (https://shopify.dev/docs/apps/build/checkout/create-cart-permalinks)
// pre-loads a cart with these exact line items when visited — sent ready-made
// so the Braze email template just links to it, no per-product logic needed.
//
// Permalinks only carry variant IDs + quantities (line-item properties are
// supported for just the first product in the URL, per Shopify's docs) — a
// protection-plan bundle needs its _bundle_id/_parent_sku/_has_assoc_protection_plan
// properties on BOTH lines to stay linked (see bfx-protection-plan.js), which a
// permalink can't reproduce. Rather than send no link at all, drop the plan
// line(s) — identified by _parent_sku, same check bfx-cart-protection-plan.js
// uses — so the email still restores the machine, just without the plan.
const buildCartPermalink = (items) => {
  if (items.length === 0) return null;

  const variantPairs = items.map((item) => `${item.variant_id}:${item.quantity}`).join(',');
  // `?storefront=true` lands the customer on /cart; a bare permalink redirects
  // straight to checkout instead, one click from paying with the protection
  // plan silently dropped above. E20-1503's recovery-link copy ("once the
  // customer lands back on the cart") assumes the cart page.
  return `${window.location.origin}${root()}cart/${variantPairs}?storefront=true`;
};

// This module fires market-wide with no template gate (theme.liquid) and needs
// no exit gesture — the 60s inactivity timer alone is enough. Shopify embeds a
// one-time token directly in the path for /account/reset/{id}/{token} and
// /account/activate/{id}/{token}, so sending the full pathname from those pages
// would leak the token to Braze. Redact the whole /account/* path rather than
// trying to allowlist which account routes happen to be token-free.
const isAccountPath = (pathname) => {
  const accountPrefix = `${root()}account`;
  return pathname === accountPrefix || pathname.startsWith(`${accountPrefix}/`);
};

const buildSource = () => {
  const { origin, pathname } = window.location;
  return isAccountPath(pathname) ? origin : `${origin}${pathname}`;
};

// /cart.js prices are whole-cent integers; Braze's own conventions (logPurchase's
// price param, the `money` Liquid filter) expect dollar-denominated decimals and
// would otherwise render these 100x too high — so convert at the boundary, once.
const centsToDollars = (cents) => cents / 100;

const buildPayload = (cart) => {
  if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return null;

  const items = restorableItems(cart);
  if (items.length === 0) return null;

  // final_line_price/original_line_price are /cart.js's own quantity-aware line
  // totals — summing item.price * item.quantity instead can drift a cent from
  // the real total when a discount doesn't divide evenly across units.
  const totalValueCents = items.reduce((sum, item) => sum + item.final_line_price, 0);
  const originalTotalPriceCents = items.reduce((sum, item) => sum + item.original_line_price, 0);

  return {
    currency: cart.currency,
    total_value: centsToDollars(totalValueCents),
    original_total_price: centsToDollars(originalTotalPriceCents),
    products: items.map((item) => ({
      product_id: item.product_id,
      sku: item.sku,
      product_name: item.product_title,
      quantity: item.quantity,
      price: centsToDollars(item.price),
      discounted_price: centsToDollars(item.discounted_price),
    })),
    source: buildSource(),
    cart_permalink: buildCartPermalink(items),
  };
};

const controller = new AbortController();
let fired = false;
let pending = false;
let inactivityTimerId = null;
let mobileIntervalId = null;

const teardown = () => {
  controller.abort();
  if (inactivityTimerId) window.clearTimeout(inactivityTimerId);
  if (mobileIntervalId) window.clearInterval(mobileIntervalId);
};

// This only confirms the event was handed to the SDK's send queue, not that
// Braze's servers received it — logCustomEvent/requestImmediateDataFlush
// expose no delivery confirmation.
const sendExitIntentEvent = (payload) =>
  sendToBraze(
    () => logCustomEventOrThrow(EVENT_NAME, payload),
    `bfx-exit-intent: ${EVENT_NAME}`,
    'bfx-exit-intent'
  );

// A bare event (no payload) still enters the customer into the abandoned-cart
// Canvas by event name alone — with nothing for it to act on, they idle out
// after 10 minutes and can't re-enter for 30 days. So neither an empty cart
// nor a failed cart fetch may ever send one: both stay armed instead,
// re-arming the inactivity fallback's one-shot timer that this firing just
// consumed, so a later trigger on the same page can still send. Also resets
// the mobile scroll gesture — otherwise, on mobile, an already-satisfied
// up-scroll condition would re-fire on every 1s interval tick indefinitely,
// polling /cart.js in a loop instead of waiting for a fresh down/up gesture.
const stayArmed = () => {
  pending = false;
  resetInactivityTimer();
  resetMobileGesture();
};

const fireExitIntent = () => {
  if (fired || pending) return;
  pending = true;

  getCart()
    .then(async (cart) => {
      // A confirmed-empty cart (or one that's empty once protection-plan-only
      // lines are filtered out) has nothing to abandon — don't fire at all.
      const payload = buildPayload(cart);
      if (!payload) {
        stayArmed();
        return;
      }

      // Only disarm once the event was actually enqueued — a valid payload
      // still isn't enough on its own, since sendExitIntentEvent() can resolve
      // false (SDK never ready) or swallow a thrown exception (send failed).
      // Either way, stay armed instead of leaving `fired` permanently true
      // over an event that never went out.
      if (await sendExitIntentEvent(payload)) {
        fired = true;
        teardown();
      } else {
        stayArmed();
      }
    })
    .catch((error) => {
      // Cart state is unknown here (fetch failed), not confirmed empty — but
      // with nothing to send, there's no reason to give up detection either.
      console.error('bfx-exit-intent: failed to load cart', error);
      stayArmed();
    });
};

// --- Inactivity fallback ---
const resetInactivityTimer = () => {
  if (inactivityTimerId) window.clearTimeout(inactivityTimerId);
  inactivityTimerId = window.setTimeout(fireExitIntent, INACTIVITY_MS);
};

const setupInactivityTimer = () => {
  const { signal } = controller;
  document.addEventListener('mousemove', resetInactivityTimer, { signal });
  document.addEventListener('mousedown', resetInactivityTimer, { signal });
  document.addEventListener('keypress', resetInactivityTimer, { signal });
  document.addEventListener('touchmove', resetInactivityTimer, { signal, passive: true });
  resetInactivityTimer();
};

// --- Desktop exit intent ---
const setupDesktopExitIntent = () => {
  document.addEventListener(
    'mouseleave',
    (event) => {
      if (typeof event.clientY === 'number' && event.clientY < 0) fireExitIntent();
    },
    { signal: controller.signal }
  );
};

// --- Mobile exit intent (scroll pattern) ---
const isMobileOrTablet = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobi/i.test(
    navigator.userAgent || ''
  );

// Hoisted (rather than local to setupMobileExitIntent) so stayArmed() can
// reset them — the interval below is level-triggered, not edge-triggered, so
// without a reset it re-fires on every tick for as long as the up-scroll
// condition stays satisfied.
let mobileDownThresholdReached = false;
let mobileDownStartY = 0;
let mobileUpStartY = 0;

const resetMobileGesture = () => {
  mobileDownThresholdReached = false;
  mobileDownStartY = window.pageYOffset || document.documentElement.scrollTop || 0;
  mobileUpStartY = 0;
};

const setupMobileExitIntent = () => {
  resetMobileGesture();

  mobileIntervalId = window.setInterval(() => {
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;
    const pageHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    if (!pageHeight) return;

    if (!mobileDownThresholdReached) {
      const downScroll = Math.max(0, y - mobileDownStartY);
      if (downScroll / pageHeight >= 0.5) {
        mobileDownThresholdReached = true;
        mobileUpStartY = y;
      }
    } else {
      const upScroll = Math.max(0, mobileUpStartY - y);
      if (upScroll / pageHeight >= 0.2) fireExitIntent();
    }
  }, 1000);
};

const run = () => {
  setupInactivityTimer();
  if (isMobileOrTablet()) setupMobileExitIntent();
  else setupDesktopExitIntent();
};

const init = () => {
  if (market !== 'us') return;
  if (document.readyState !== 'loading') run();
  else
    document.addEventListener('DOMContentLoaded', run, { once: true, signal: controller.signal });
};

init();
